import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { getGamePhraseHistory } from '../../services/contents_api';
import PhraseEditConfirmDialog from './PhraseEditConfirmDialog';

interface PhraseHistoryProps {
  phraseId: number;
  currentPhrase: {
    content: string;
    context: string | null;
  };
}

interface HistoryItem {
  id: number;
  editor: string;
  action_type: number;
  content: string;
  context: string | null;
  source: string | null;
  created_at: string;
}

const PhraseHistory: React.FC<PhraseHistoryProps> = ({ phraseId, currentPhrase }) => {
  const [histories, setHistories] = useState<HistoryItem[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<HistoryItem | null>(null);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const historyData = await getGamePhraseHistory(phraseId);
        setHistories(historyData);
      } catch (error) {
        console.error('データの取得に失敗しました:', error);
      }
    };

    loadData();
  }, [phraseId]);

  const handleHistoryClick = (history: HistoryItem, index: number) => {
    setSelectedHistory(history);
    setDiffDialogOpen(true);
  };

  const getComparisonData = (currentIndex: number) => {
    const currentHistory = histories[currentIndex];
    
    if (currentIndex === 0) {
      return {
        originalData: {
          content: currentHistory.content,
          context: currentHistory.context
        },
        newData: {
          content: currentPhrase.content,
          context: currentPhrase.context
        }
      };
    }

    const nextHistory = histories[currentIndex - 1];
    return {
      originalData: {
        content: currentHistory.content,
        context: currentHistory.context
      },
      newData: {
        content: nextHistory.content,
        context: nextHistory.context
      }
    };
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        編集履歴
      </Typography>
      {histories.map((history, index) => (
        <Box 
          key={history.id}
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            '&:hover': { bgcolor: 'action.hover' },
            cursor: 'pointer'
          }}
          onClick={() => handleHistoryClick(history, index)}
        >
          <IconButton size="small">
            <HistoryIcon fontSize="small" />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2">
              {history.editor} が{history.action_type === 1 ? '作成' : '編集'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(history.created_at).toLocaleString('ja-JP')}
            </Typography>
          </Box>
        </Box>
      ))}

      {selectedHistory && (
        <PhraseEditConfirmDialog
          open={diffDialogOpen}
          onClose={() => setDiffDialogOpen(false)}
          onConfirm={() => setDiffDialogOpen(false)}
          {...getComparisonData(histories.findIndex(h => h.id === selectedHistory.id))}
          readOnly
        />
      )}
    </Box>
  );
};

export default PhraseHistory; 